import { generatePath, useLocation, useNavigate, useParams, Path } from "react-router-dom";
import { type CellContext } from "@tanstack/react-table";
import Table, {
  TableControls,
  TableFooter,
  toTableControlActions,
  buildAvatarDetailsColumn,
  buildCopyTextColumn,
  buildActionColumn
} from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import Button from "@/components/Button";
import SearchInput from "@/components/SearchInput";
import usePagination from "@/hooks/usePagination";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { useQuery, useMutation } from "@apollo/client";
import { EVENT_LEADS_QUERY, CANCEL_EVENT_REGISTRATION_MUTATION } from "../../queries";
import { useEventDetailQueryDocument } from "../../useEventDetailQuery";
import Dialog from "@/components/Dialog";
import { useState, useEffect } from "react";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { toLocaleDate } from "@/lib/dateHelpers";
import { useTopActionBar, SideBar } from "@/layouts/AppLayout/TopActionBarContext";
import Progress from "@/components/Progress";
import { downloadCsv } from "../../utils";
import { RegistrationStatus } from "@/pages/events/[eventSlug]/types.ts";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";

interface LeadRow {
  id: string;
  name: string;
  pgaId: string;
  email: string;
  phone: string;
  zipCode: string;
  referralSource: string;
  submitted: string;
  profilePhoto?: string;
}

// CSV export configuration
const CSV_COLUMN_HEADERS = [
  "First Name",
  "Last Name",
  "PGA ID",
  "Email",
  "Phone",
  "Zip Code",
  "External ID",
  "Event Title"
];

const generateCsvData = (leads: LeadRow[], registrantData: any[], eventTitle: string) => {
  return leads.map((lead) => {
    // Find the corresponding registrant data for externalId
    const registrant = registrantData.find((reg: any) => reg != null && reg.id === lead.id);

    // Split name into first and last name
    const nameParts = lead.name.split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    return [
      firstName,
      lastName,
      lead.pgaId,
      lead.email,
      lead.phone,
      lead.zipCode,
      registrant?.externalId || "",
      eventTitle
    ];
  });
};

export default function LeadsPage() {
  const { eventSlug = "" } = useParams();
  const { isCompactLayout } = useIsCompactLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const { createFooterPagination, resetPagination } = usePagination();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });
  const [variables, setVariables] = useState<{ search?: string }>({});

  const { Menu, setMenu } = useTableRowMenu();
  const { showSidebar } = useTopActionBar();

  const [removeRegistrationDialogOpen, setRemoveRegistrationDialogOpen] = useState(false);
  const [leadToRemove, setLeadToRemove] = useState<LeadRow | null>(null);
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | null>(null);

  useEffect(() => {
    resetPagination();
    setVariables(debouncedSearch ? { search: debouncedSearch } : {});
  }, [debouncedSearch, resetPagination]);

  const [cancelEventRegistrationMutation, { loading: loadingCancelRegistration }] = useMutation(
    CANCEL_EVENT_REGISTRATION_MUTATION
  );

  // Cleanup leadToRemove when dialog closes
  useEffect(() => {
    if (!removeRegistrationDialogOpen) {
      setLeadToRemove(null);
    }
  }, [removeRegistrationDialogOpen]);

  const {
    data: leadsData,
    loading: leadsLoading,
    refetch: refetchLeads
  } = useQuery(EVENT_LEADS_QUERY, {
    variables: {
      slug: eventSlug,
      status: RegistrationStatus.REGISTERED,
      ...variables
    },
    errorPolicy: "all"
  });

  // Fetch event data to get the event title
  const eventDetailQuery = useEventDetailQueryDocument();

  const { data: eventData } = useQuery(eventDetailQuery, {
    variables: { slug: eventSlug }
  });

  const allRegistrants = leadsData?.eventRegistrants?.nodes || [];

  const leads = allRegistrants
    .filter((registrant: any) => registrant != null)
    .map((registrant: any) => {
      const primaryPhone =
        registrant.phoneNumbers?.find((phone: any) => phone.phoneType === "mobile")?.phoneFormatted ||
        registrant.phoneNumbers?.find((phone: any) => phone.phoneType === "home")?.phoneFormatted ||
        registrant.phoneNumbers?.[0]?.phoneFormatted ||
        "";

      const transformedLead = {
        id: registrant.id,
        name: registrant.registrantName || "Unknown",
        pgaId: registrant.pgaId || "Lead",
        email: registrant.email || "",
        phone: primaryPhone,
        zipCode: registrant.recruitmentLead?.zipcode || "",
        referralSource: registrant.recruitmentLead?.referralSource || "",
        submitted: registrant.registeredAt
          ? toLocaleDate(registrant.registeredAt, { year: "numeric", month: "short", day: "numeric" })
          : "N/A",
        profilePhoto: registrant.profilePhoto
      };

      return transformedLead;
    });

  const handleCsvExport = () => {
    downloadCsv(
      [
        CSV_COLUMN_HEADERS,
        ...generateCsvData(
          leads,
          allRegistrants.filter((reg: any) => reg != null),
          eventData?.event?.title || eventSlug
        )
      ],
      `event-leads-${eventSlug}-${Date.now()}.csv`
    );
    showSnackbar("Leads exported");
  };

  function hasPersonProfile(pgaId: string | null | undefined): pgaId is string {
    return !!pgaId && pgaId !== "Lead";
  }

  function buildPersonPath(pgaId: string | null | undefined): Path | null {
    if (!hasPersonProfile(pgaId)) {
      return null;
    }
    const returnTo = location.pathname;
    return generatePath(`/people/:pgaId/person/overview?return_to=${encodeURIComponent(returnTo)}`, {
      pgaId
    }) as unknown as Path;
  }

  const handleViewPerson = (lead: LeadRow) => {
    const personPath = buildPersonPath(lead.pgaId);
    if (personPath) {
      navigate(personPath);
    }
  };

  const handleAddInteraction = (lead: LeadRow) => {
    showSidebar(SideBar.CREATE_GLOBAL_INTERACTION, { pgaId: lead.pgaId })();
  };

  const handleRemoveRegistration = (lead: LeadRow) => {
    setLeadToRemove(lead);
    setRemoveRegistrationDialogOpen(true);
  };

  const columns = [
    ...buildAvatarDetailsColumn<LeadRow>({
      isCompactLayout,
      getTitle: (row) => row.name,
      getSubtitle: (row) => row.pgaId,
      subtitleLabel: "PGA ID",
      getPicture: (row) => row.profilePhoto,
      header: "Name",
      size: 280,
      minSize: 200,
      id: "name"
    }),
    buildCopyTextColumn<LeadRow>({
      accessorKey: "pgaId",
      header: "PGA ID",
      getText: (row) => row.pgaId,
      copyFieldLabel: "PGA ID",
      enableSorting: false
    }),
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }: CellContext<LeadRow, unknown>) => row.original.email
    },
    {
      accessorKey: "phone",
      header: "Phone",
      cell: ({ row }: CellContext<LeadRow, unknown>) => row.original.phone
    },
    {
      accessorKey: "zipCode",
      header: "Zip Code",
      cell: ({ row }: CellContext<LeadRow, unknown>) => row.original.zipCode
    },
    {
      accessorKey: "referralSource",
      header: "Referral Source",
      cell: ({ row }: CellContext<LeadRow, unknown>) => row.original.referralSource
    },
    {
      accessorKey: "submitted",
      header: "Submitted",
      cell: ({ row }: CellContext<LeadRow, unknown>) => row.original.submitted
    },
    buildActionColumn<LeadRow>({
      getActions: (lead) =>
        lead.pgaId === "Lead"
          ? [
              {
                text: "Remove Registration",
                section: "Actions",
                iconName: "delete",
                destructive: true,
                onClick: () => handleRemoveRegistration(lead)
              }
            ]
          : [
              {
                text: "View Person",
                section: "Actions",
                onClick: () => handleViewPerson(lead)
              },
              {
                text: "Add Interaction",
                section: "Actions",
                onClick: () => handleAddInteraction(lead)
              },
              {
                text: "Remove Registration",
                section: "Actions",
                iconName: "delete",
                destructive: true,
                onClick: () => handleRemoveRegistration(lead)
              }
            ],
      onViewClick: (lead) => {
        if (hasPersonProfile(lead.pgaId)) {
          setSelectedPgaId(lead.pgaId);
          setIsPersonSideSheetOpen(true);
        }
      },
      showViewButton: (lead) => hasPersonProfile(lead.pgaId),
      viewButtonTitle: "View person details",
      actionId,
      setMenu
    })
  ];

  const removeRegistration = async () => {
    if (!leadToRemove) return;

    try {
      const { data } = await cancelEventRegistrationMutation({
        variables: {
          id: leadToRemove.id,
          issueRefund: true,
          suspend: false
        }
      });

      if (data?.cancelEventRegistration?.success) {
        showSnackbar(`Registration removed for ${leadToRemove.name}`);

        // Close the dialog and reset state
        setRemoveRegistrationDialogOpen(false);
        setLeadToRemove(null);

        // Refresh the leads list after successful removal
        refetchLeads();
      } else {
        const apiMessage = data?.cancelEventRegistration?.message;
        if (apiMessage) {
          showErrorSnackbar(apiMessage);
        } else {
          showErrorSnackbar("Registration not removed", { subtitle: "Please try again later." });
        }
      }
    } catch (error) {
      console.error("Error removing registration:", error);
      showErrorSnackbar("Registration not removed", { subtitle: "Please try again later." });
    }
  };

  const paginationData = {
    edges: leads.map((lead) => ({ node: lead })),
    pageInfo: { hasNextPage: false, hasPreviousPage: false, startCursor: undefined, endCursor: undefined },
    totalCount: leadsData?.eventRegistrants?.totalCount || leads.length
  };

  return (
    <>
      <Menu />
      <div className="pt-12" />
      <Table
        data={leads}
        columns={columns}
        loading={leadsLoading}
        hasActiveFilters={!!debouncedSearch}
        getRowLink={(row) => {
          const personPath = buildPersonPath(row.original.pgaId);
          return personPath ? { to: personPath } : null;
        }}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Leads Found" caption="This event does not have any leads" iconName="person" />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="search" />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            actions={toTableControlActions([
              {
                label: "Export Current List",
                icon: "download",
                variant: "text",
                onClick: handleCsvExport
              }
            ])}
            activeFilters={debouncedSearch ? 1 : 0}
            onClearAllFilters={() => {
              resetPagination();
              clearSearch();
            }}
            onSortClick={onSortClick}
          >
            <SearchInput
              placeholder="Search Leads"
              query={searchValue}
              onChange={(value) => {
                setSearchValue(value);
                resetPagination();
              }}
              onClear={() => {
                clearSearch();
                resetPagination();
              }}
            />
          </TableControls>
        )}
        renderFooter={() => (
          <TableFooter pagination={createFooterPagination(paginationData, { pageCount: leads.length })} />
        )}
      />

      <Dialog
        open={removeRegistrationDialogOpen}
        onClosed={() => setRemoveRegistrationDialogOpen(false)}
        headline="Remove Registration"
        actions={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setRemoveRegistrationDialogOpen(false)} variant="text">
              Cancel
            </Button>
            <Button
              onClick={removeRegistration}
              variant="filled"
              icon={loadingCancelRegistration ? <Progress size={20} color="white" /> : "delete"}
              theme="error"
              disabled={loadingCancelRegistration}
            >
              {loadingCancelRegistration ? "Removing..." : "Remove"}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-2">
          <p className="text-body-medium text-sys-brand-on-surface">
            Are you sure you want to remove this lead from this event? This cannot be undone.
          </p>
          <p className="text-body-medium text-sys-brand-on-surface">
            <strong>Note:</strong> The person will not be deleted in the overall system.
          </p>
        </div>
      </Dialog>
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(null);
        }}
        pgaId={selectedPgaId}
      />
    </>
  );
}
